import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  buildColumnsForFormDetail,
  exportFormDetailToCsv,
  flattenOffpaperValues,
  formatCellValue,
  formatOffpaperDateTime,
  getOffpaperFormDetail,
  listOffpaperForms,
  syncAllOffpaper,
  type OffpaperForm,
  type OffpaperFormDetail,
} from "@/lib/offpaper";
import { toast } from "@/lib/toast";

export function OffpaperIndexPage() {
  const [forms, setForms] = useState<OffpaperForm[]>([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [formsError, setFormsError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OffpaperFormDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);

  const totalLocal = useMemo(
    () => forms.reduce((sum, f) => sum + Number(f.submission_count || 0), 0),
    [forms],
  );

  const loadForms = useCallback(async (options?: { silent?: boolean; keepSelection?: boolean }) => {
    if (!options?.silent) setLoadingForms(true);
    setFormsError(null);
    try {
      const rows = await listOffpaperForms();
      const list = Array.isArray(rows) ? rows : [];
      setForms(list);

      if (!list.length) {
        setSelectedId(null);
        setDetail(null);
        return;
      }

      setSelectedId((prev) => {
        if (options?.keepSelection && prev && list.some((f) => f.offpaper_id === prev)) {
          return prev;
        }
        return list[0]?.offpaper_id ?? null;
      });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Formulare konnten nicht geladen werden.";
      setFormsError(message);
      if (!options?.silent) toast.error(message);
    } finally {
      setLoadingForms(false);
    }
  }, []);

  const loadDetail = useCallback(async (formOffpaperId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoadingDetail(true);
    setDetailError(null);
    try {
      const data = await getOffpaperFormDetail(formOffpaperId);
      setDetail(data);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Einträge konnten nicht geladen werden.";
      setDetailError(message);
      setDetail(null);
      if (!options?.silent) toast.error(message);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void loadForms();
  }, [loadForms]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  async function handleSyncFromOffpaper() {
    setSyncing(true);
    try {
      const result = await syncAllOffpaper();
      const inserted = Number(result.inserted_submissions || 0);
      toast.success(
        inserted > 0
          ? `${inserted} neue Einträge aus Offpaper importiert.`
          : "Keine neuen Einträge – lokale Daten sind aktuell.",
      );
      await loadForms({ silent: true, keepSelection: true });
      if (selectedId) await loadDetail(selectedId, { silent: true });
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Synchronisation von Offpaper ist fehlgeschlagen.",
      );
    } finally {
      setSyncing(false);
    }
  }

  function handleExport() {
    if (!detail?.submissions?.length) {
      toast.error("Für dieses Formular sind keine Einträge vorhanden.");
      return;
    }
    try {
      exportFormDetailToCsv(detail);
      toast.success("CSV-Export gestartet.");
    } catch {
      toast.error("Beim Excel-Export ist ein Fehler aufgetreten.");
    }
  }

  const columns = useMemo(
    () => (detail ? buildColumnsForFormDetail(detail) : []),
    [detail],
  );

  return (
    <AppShell
      title="Offpaper Formulare"
      subtitle={
        <>
          Links ein Formular auswählen – rechts siehst du alle lokal gespeicherten Einträge und die
          Platzhalter pro Feld. Lokal: {totalLocal.toLocaleString("de-DE")} Einträge.
        </>
      }
      back={{ to: "/wartungen", label: "zurück zu Wartungen" }}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadForms({ keepSelection: true })}
            disabled={loadingForms || syncing}
          >
            Neu laden
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={syncing}
            onClick={() => void handleSyncFromOffpaper()}
            disabled={loadingForms}
          >
            Von Offpaper synchronisieren
          </Button>
        </div>
      }
    >
      <Alert variant="info" className="text-sm">
        <strong className="font-medium">Hinweis:</strong> „Von Offpaper synchronisieren“ importiert
        neue Formulareinträge aus der Offpaper-Cloud (
        <code className="text-xs">/offpaper/sync-all</code>
        ). Der geplante Aufruf{" "}
        <code className="text-xs">/admin/sync-offpaper-submissions</code> lädt bereits lokale PDFs
        nach SharePoint hoch (Projektordner <code className="text-xs">Wartungsprotokolle</code>{" "}
        sowie InfoHub „Im Projekt abgelegt“ / „Nicht im Projekt abgelegt“) und erhöht die
        Eintragsanzahl nicht.
      </Alert>

      <Card
        title="Formulare & Einträge"
        description="Sidebar mit Formularen, rechts die Tabellenansicht inkl. Export."
        bodyClassName="p-0"
      >
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
          <aside className="border-b border-separator lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <span className="text-sm font-semibold text-label">Formulare</span>
              <span className="text-xs text-label-tertiary">
                {forms.length.toLocaleString("de-DE")}
              </span>
            </div>
            <div className="max-h-[70vh] space-y-1 overflow-y-auto px-2 pb-3">
              {loadingForms ? (
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-label-secondary">
                  <Spinner /> Formulare werden geladen …
                </div>
              ) : formsError ? (
                <p className="px-2 py-2 text-sm text-status-red">{formsError}</p>
              ) : forms.length === 0 ? (
                <p className="px-2 py-2 text-sm text-label-secondary">Keine Formulare gefunden.</p>
              ) : (
                forms.map((form) => {
                  const active = form.offpaper_id === selectedId;
                  return (
                    <button
                      key={form.offpaper_id}
                      type="button"
                      onClick={() => setSelectedId(form.offpaper_id)}
                      className={clsx(
                        "w-full rounded-lg px-3 py-2 text-left transition",
                        active
                          ? "bg-accent-soft text-label ring-1 ring-accent"
                          : "hover:bg-fill-tertiary",
                      )}
                    >
                      <div className="text-sm font-semibold">
                        {form.name?.trim() || "(Ohne Namen)"}
                      </div>
                      <div className="text-xs text-label-tertiary">
                        {Number(form.submission_count || 0).toLocaleString("de-DE")} Einträge
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="min-w-0 px-4 py-4">
            {!selectedId ? (
              <p className="text-sm text-label-secondary">Bitte links ein Formular auswählen.</p>
            ) : loadingDetail ? (
              <div className="flex items-center gap-2 text-sm text-label-secondary">
                <Spinner /> Einträge werden geladen …
              </div>
            ) : detailError ? (
              <p className="text-sm text-status-red">{detailError}</p>
            ) : !detail ? (
              <p className="text-sm text-label-secondary">Keine Daten geladen.</p>
            ) : (
              <div className="space-y-4">
                <header className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-label">
                      {detail.form?.name?.trim() || "(Ohne Namen)"}
                    </h3>
                    {detail.form?.description ? (
                      <p className="mt-1 text-sm text-label-secondary">{detail.form.description}</p>
                    ) : null}
                  </div>
                  <div className="text-sm text-label-secondary">
                    {(detail.submissions?.length || 0).toLocaleString("de-DE")} Einträge
                  </div>
                </header>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-label">Einträge (Tabellenansicht)</h4>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleExport}
                    disabled={!detail.submissions?.length}
                  >
                    Excel-Export
                  </Button>
                </div>

                {!detail.submissions?.length ? (
                  <p className="text-sm text-label-secondary">
                    Für dieses Formular wurden noch keine Einträge synchronisiert.
                  </p>
                ) : (
                  <div className="max-w-full overflow-x-auto rounded-lg ring-1 ring-separator">
                    <table className="w-max min-w-full border-collapse text-xs">
                      <thead className="bg-fill-tertiary/60">
                        <tr>
                          {columns.map((col) => (
                            <th
                              key={col.key}
                              className="whitespace-nowrap px-2 py-2 text-left align-bottom font-semibold text-label"
                            >
                              <div className="flex flex-col gap-0.5">
                                {col.placeholder ? (
                                  <code className="rounded-full bg-card px-1.5 py-0.5 text-[10px] font-normal text-label-secondary ring-1 ring-separator">
                                    {col.placeholder}
                                  </code>
                                ) : null}
                                <span>{col.label}</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.submissions.map((sub, idx) => {
                          const flat = flattenOffpaperValues(sub.values || {});
                          return (
                            <tr
                              key={sub.id || sub.offpaper_id || idx}
                              className="border-t border-separator hover:bg-fill-tertiary/40"
                            >
                              {columns.map((col) => {
                                let raw: unknown;
                                switch (col.key) {
                                  case "__idx":
                                    raw = idx + 1;
                                    break;
                                  case "__label":
                                    raw = sub.label || "";
                                    break;
                                  case "__created_at":
                                    raw = formatOffpaperDateTime(sub.created_at);
                                    break;
                                  default:
                                    raw = flat[col.key] != null ? flat[col.key] : "";
                                    break;
                                }
                                return (
                                  <td
                                    key={col.key}
                                    className="max-w-[220px] truncate whitespace-nowrap px-2 py-1.5 text-label"
                                    title={formatCellValue(raw)}
                                  >
                                    {formatCellValue(raw)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </Card>
    </AppShell>
  );
}
